import { useEffect, useMemo, useState } from 'react'
import { Logger } from '@/components/Logger'
import { useGetUserSelf } from '@/hooks/v1'
import { getCashReserveSelfQueryKey } from '@/hooks/v1/hooks/useGetCashReserveSelf'
import { getLedgerBalancesSelfQueryKey } from '@/hooks/v1/hooks/useGetLedgerBalancesSelf'
import { getLedgerPositionsSelfQueryKey } from '@/hooks/v1/hooks/useGetLedgerPositionsSelf'
import type { Portfolio } from '@/hooks/v1/types/Portfolio'
import type { Position } from '@/hooks/v1/types/Position'
import { queryClient } from '@/services/api'
import { subscribeRealtime } from '@/services/api/realtimeSubscription'
import { findBalanceIndex } from '@/utils/accountBalanceSafety'
import { isWebSocketErrorMessage } from './useWebSocketStatus'

const USER_SELF_RETRY_LIMIT = 5
const USER_SELF_RETRY_DELAY_MS = 1500

type UseUserBalanceSubscriptionOptions = {
  enabled?: boolean
}

type BalanceStreamMessageItem = {
  Val?: Position
}

const isPositionUpdate = (value: unknown): value is Position =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'asset_id' in (value as Record<string, unknown>) &&
      (value as Record<string, unknown>).asset_id,
  )

const hasValWrapper = (value: unknown): value is BalanceStreamMessageItem =>
  Boolean(value && typeof value === 'object' && 'Val' in (value as Record<string, unknown>))

const extractBalanceUpdates = (message: unknown): Position[] => {
  if (Array.isArray(message)) {
    return message
      .map((item) => (hasValWrapper(item) ? item.Val : undefined))
      .filter(isPositionUpdate)
  }

  if (hasValWrapper(message) && isPositionUpdate(message.Val)) {
    return [message.Val]
  }

  if (isPositionUpdate(message)) {
    return [message]
  }

  return []
}

const updateBalancesCache = (prev: any, updates: Position[]) => {
  if (updates.length === 0) {
    return prev
  }

  const prevBalances: Position[] = Array.isArray(prev?.data?.balances) ? prev.data.balances : []
  const updatedBalances = [...prevBalances]
  let changed = false

  for (const balanceUpdate of updates) {
    if (!balanceUpdate?.id || !balanceUpdate?.asset_id) {
      continue
    }

    const existingIndex = findBalanceIndex(updatedBalances, balanceUpdate)

    if (existingIndex >= 0) {
      const merged = {
        ...updatedBalances[existingIndex],
        ...balanceUpdate,
      }

      // Skip if nothing actually changed
      const current = updatedBalances[existingIndex]
      const hasDiff = Object.entries(balanceUpdate as Record<string, unknown>).some(
        ([key, value]) => (current as Record<string, unknown>)[key] !== value,
      )

      if (!hasDiff) {
        continue
      }

      updatedBalances[existingIndex] = merged
      changed = true
    } else {
      Logger.log('Debug: useUserBalanceSubscription - Adding new balance')
      updatedBalances.push(balanceUpdate)
      changed = true
    }
  }

  if (!changed) {
    return prev
  }

  const nextData = {
    ...(prev?.data ?? {}),
    balances: updatedBalances,
  }

  if (prev && typeof prev === 'object') {
    return {
      ...prev,
      data: nextData,
    }
  }

  return { data: nextData }
}

type PositionsByScope = NonNullable<Portfolio['position']>

const updatePortfolioCache = (prev: any, updates: Position[]) => {
  if (updates.length === 0) {
    return prev
  }

  const prevData = (prev?.data ?? {}) as { portfolio?: Portfolio }
  const prevPortfolio = (prevData.portfolio ?? {}) as Portfolio
  const prevPositions = (prevPortfolio.position ?? {}) as PositionsByScope
  const nextPositions: PositionsByScope = { ...prevPositions }
  let changed = false

  for (const balanceUpdate of updates) {
    const positionId = balanceUpdate?.id
    const assetId = balanceUpdate?.asset_id

    if (!positionId || !assetId) {
      continue
    }

    const existingAssets = nextPositions[positionId]
    const currentEntry = existingAssets?.[assetId]

    const updateDiffers =
      !currentEntry ||
      Object.entries(balanceUpdate as Record<string, unknown>).some(
        ([key, value]) => (currentEntry as Record<string, unknown>)[key] !== value,
      )

    if (!updateDiffers) {
      continue
    }

    nextPositions[positionId] = {
      ...(existingAssets ?? {}),
      [assetId]: {
        ...(currentEntry ?? {}),
        ...balanceUpdate,
      },
    }
    changed = true
  }

  if (!changed) {
    return prev
  }

  const nextPortfolio: Portfolio = {
    ...prevPortfolio,
    position: nextPositions,
  }

  const nextData = {
    ...prevData,
    portfolio: nextPortfolio,
  }

  if (prev && typeof prev === 'object') {
    return {
      ...prev,
      data: nextData,
    }
  }

  return { data: nextData }
}

export const useUserBalanceSubscription = ({
  enabled = true,
}: UseUserBalanceSubscriptionOptions = {}) => {
  const { data: userSelfData, refetch: refetchUserSelf } = useGetUserSelf()
  const userSelf = useMemo(() => userSelfData?.data ?? null, [userSelfData])
  const userId = useMemo(() => userSelf?.id ?? null, [userSelf])
  const [userSelfRetryCount, setUserSelfRetryCount] = useState(0)

  useEffect(() => {
    // Reset retries when user changes or when we finally have an id
    setUserSelfRetryCount(0)
  }, [userId])

  useEffect(() => {
    if (!enabled || userId || userSelfRetryCount >= USER_SELF_RETRY_LIMIT) {
      return
    }

    const timer = setTimeout(() => {
      Logger.log('Debug: useUserBalanceSubscription - retrying user self fetch', {
        attempt: userSelfRetryCount + 1,
      })
      refetchUserSelf().catch((error) => {
        Logger.error('Debug: useUserBalanceSubscription - user self refetch failed', error)
      })
      setUserSelfRetryCount((count) => count + 1)
    }, USER_SELF_RETRY_DELAY_MS)

    return () => clearTimeout(timer)
  }, [enabled, refetchUserSelf, userId, userSelfRetryCount])

  useEffect(() => {
    if (!enabled) {
      return
    }

    // Only subscribe if user is authenticated and has a uid
    if (!userId) {
      Logger.log('DEBUG WS BALANCE: No user UID, skipping subscription')
      return
    }

    // Connect to WebSocket using centralized client
    const unsubscribe = subscribeRealtime(
      {
        onMessage: (data) => {
          try {
            // Track data update for connection health monitoring

            // Check if message contains an error before processing
            if (isWebSocketErrorMessage(data)) {
              Logger.error(
                'Debug: useUserBalanceSubscription - Received error message, skipping setQueryData:',
                data,
              )
              return
            }

            // Check for empty messages
            if (
              !data ||
              (typeof data === 'object' && Object.keys(data).length === 0) ||
              data === ''
            ) {
              return
            }

            const balanceUpdates = extractBalanceUpdates(data)

            if (balanceUpdates.length === 0) {
              return
            }

            const ledgerBalancesKey = getLedgerBalancesSelfQueryKey()
            void queryClient.invalidateQueries({ queryKey: getCashReserveSelfQueryKey() })
            const ledgerPositionsKey = getLedgerPositionsSelfQueryKey()

            queryClient.setQueryData(ledgerBalancesKey, (prev: any) =>
              updateBalancesCache(prev, balanceUpdates),
            )

            queryClient.setQueryData(ledgerPositionsKey, (prev: any) =>
              updatePortfolioCache(prev, balanceUpdates),
            )
          } catch (error) {
            Logger.error(
              'Debug: useUserBalanceSubscription - Error parsing WebSocket message:',
              error,
            )
          }
        },
        onOpen: () => {
          Logger.log(
            'Debug: useUserBalanceSubscription - WebSocket connected successfully to user ledger stream',
          )
        },
        onError: (error) => {
          Logger.error('Debug: useUserBalanceSubscription - WebSocket error:', error)
        },
        onClose: (event) => {
          Logger.log(
            'Debug: useUserBalanceSubscription - WebSocket connection closed, code:',
            event.code,
            'wasClean:',
            event.wasClean,
          )
        },
      },
      {
        key: `accounts-balance:${userId}`,
        path: '/accounts/balance',
        subscribeData: { subscribe: [userId] },
        unsubscribeData: { unsubscribe: [userId] },
        mapNotification: (data) => {
          if (!data || typeof data !== 'object' || !('balances' in data)) return data
          const balancesByUser = (data as { balances: Record<string, unknown> }).balances
          const balances = Array.isArray(balancesByUser?.[userId]) ? balancesByUser[userId] : []
          return (balances as unknown[]).map((balance) => ({ Val: balance }))
        },
      },
    )

    // Cleanup function to unsubscribe
    return () => {
      Logger.log('Debug: useUserBalanceSubscription - Unsubscribing from ledger subscription')
      unsubscribe()
    }
  }, [enabled, userId])
}
